/**
 * API工具模块
 * 负责所有API请求的封装和处理
 * 提供统一的请求/响应拦截、错误处理和Mock数据支持
 * 使用axios作为HTTP客户端
 */

// 导入axios HTTP客户端
import axios from 'axios';
import {sm4Decrypt, sm4Encrypt,getKey} from '@/utils/sm4'
import {doSM2Encrypt,doSM2DecryptStr} from "@/utils/sm2";
/**
 * 图片URL处理函数
 * 确保图片地址有正确的前缀
 * @param {string} imgUrl - 原始图片地址
 * @returns {string} 处理后的完整图片地址
 */
const processImageUrl = (imgUrl) => {
  // 如果是空值，直接返回
  if (!imgUrl) {
    return imgUrl;
  }
  
  // 检查是否是表情符号（使用更兼容的方式）
  // 判断字符串长度是否为1且Unicode值是否在表情符号范围内
  if (imgUrl.length === 1) {
    const codePoint = imgUrl.codePointAt(0);
    // 基本表情符号范围检测
    if (
      (codePoint >= 0x1F300 && codePoint <= 0x1F6FF) ||
      (codePoint >= 0x1F900 && codePoint <= 0x1F9FF) ||
      (codePoint >= 0x2600 && codePoint <= 0x26FF) ||
      (codePoint >= 0x2700 && codePoint <= 0x27BF)
    ) {
      return imgUrl;
    }
  }
  
  // 如果已经是完整的URL，直接返回
  if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://')) {
    return imgUrl;
  }
  
  // 添加前缀，确保正确拼接
  const prefix = 'https://sdjqr.zczhcs.com/prod-api';
  // 处理可能的重复斜杠
  return imgUrl.startsWith('/') ? `${prefix}${imgUrl}` : `${prefix}/${imgUrl}`;
};

/**
 * 根据环境设置API基础URL
 * 使用Vue CLI提供的环境变量(VUE_APP_API_BASE_URL)
 * 如果环境变量未设置，则使用默认值
 */
const getBaseURL = () => {
  // 优先使用环境变量中配置的API地址
  // VUE_APP_API_BASE_URL会根据.env.development或.env.production自动设置
  return process.env.VUE_APP_API_BASE_URL || '/api';
};

/**
 * 创建axios实例
 * 配置基础URL、超时时间和默认请求头
 */
const api = axios.create({
  baseURL: getBaseURL(), // 根据环境动态设置基础URL
  timeout: 10000, // 请求超时时间设置为10秒，避免请求长时间挂起
  headers: {
    'Content-Type': 'application/json' // 默认请求内容类型为JSON格式
  }
});

/**
 * 请求拦截器
 * 作用：在每个API请求发送前执行预处理逻辑
 * 主要功能：自动添加身份认证token、请求日志记录等
 */
api.interceptors.request.use(
  config => {
    // 从本地存储获取认证token
    // 在实际应用中，token通常是从用户登录后获取并存储的
    const token = localStorage.getItem('auth_token');
    const key = getKey();
    const iv = getKey();
    // 如果token存在，则添加到请求头的Authorization字段
    // 使用Bearer认证方案，这是JWT等认证机制的标准做法
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    config.headers['accessToken'] = doSM2Encrypt(key+' '+ iv);
    //执行加密方法
    if (config.method === 'post') {
       const datas = {
        requestData: sm4Encrypt(config.data,key,iv)
        }
        config.data = datas;
    }
    return config; // 返回处理后的配置
  },
  error => {
    // 处理请求配置阶段的错误
    // 例如：请求参数验证失败、网络连接问题等
    return Promise.reject(error);
  }
);

/**
 * 递归处理对象中的图片URL
 * @param {Object|Array} data - 要处理的数据对象或数组
 * @returns {Object|Array} 处理后的数据
 */
const processDataImages = (data) => {
  // 如果是null或undefined，直接返回
  if (!data) return data;
  
  // 如果是数组，递归处理每个元素
  if (Array.isArray(data)) {
    return data.map(processDataImages);
  }
  
  // 如果是对象，递归处理每个属性
  if (typeof data === 'object') {
    const processedData = { ...data };
    
    // 遍历对象的所有属性
    for (const key in processedData) {
      if (Object.prototype.hasOwnProperty.call(processedData, key)) {
        // 如果是img相关属性，处理URL
        if (key.toLowerCase().includes('img') || key.toLowerCase().includes('image') || key.toLowerCase().includes('avatar')) {
          if (typeof processedData[key] === 'string') {
            processedData[key] = processImageUrl(processedData[key]);
          }
        } else {
          // 递归处理嵌套对象或数组
          processedData[key] = processDataImages(processedData[key]);
        }
      }
    }
    
    return processedData;
  }
  
  // 基本类型，直接返回
  return data;
};

/**
 * 响应拦截器
 * 作用：在收到API响应后统一处理
 * 主要功能：简化响应数据结构、统一错误处理、认证失效处理等
 */
api.interceptors.response.use(
  // 成功响应处理
  response => {
    // 获取响应数据
   // const responseData = response.data;
    console.log("解密前key数据:",response.data.data.enterKey)
    const enterKey = doSM2DecryptStr(response.data.data.enterKey);
    const [key, iv] = enterKey.split(' ');
    console.log("解密前数据:",response.data.data.responseData)
     const responseData = sm4Decrypt(response.data.data.responseData,key,iv)
     console.log("解密后数据:",responseData)
    // 处理响应数据中的图片URL
    const processedData = processDataImages(responseData);
    
    return processedData;
  },
  
  // 错误响应处理
  error => {
    // 处理HTTP 401未授权错误
    // 这通常表示token已过期或无效
    if (error.response && error.response.status === 401) {
      // 清除本地存储的无效token
      localStorage.removeItem('auth_token');
      // 在实际应用中，这里可以触发登录弹窗或自动重定向到登录页面
      console.error('身份验证失败，请重新登录');
    }
    
    // 其他类型的错误通过Promise rejection传递给调用方
    // 允许调用方根据业务需求进行自定义错误处理
    return Promise.reject(error);
  }
);

/**
 * Mock数据 - 用于开发测试环境
 * 在实际后端API未就绪时，提供模拟数据支持开发和测试
 */
const mockPrizes = [
  { id: 1, name: '一等奖', img: '🏆',awardsName:'1' }, // 一等奖数据
  { id: 2, name: '二等奖', img: '🎁',awardsName:'2' }, // 二等奖数据
  { id: 3, name: '三等奖', img: '🎉',awardsName:'3' }, // 三等奖数据
  { id: 4, name: '参与奖', img: '🎈',awardsName:'4' }, // 参与奖数据
  { id: 5, name: '幸运奖', img: '🍀',awardsName:'5' }, // 幸运奖数据
  { id: 6, name: '鼓励奖', img: '💪',awardsName:'6' }  // 鼓励奖数据
];

/**
 * 抽奖相关API方法集合
 * 封装所有与抽奖功能相关的接口调用
 */
export const lotteryAPI = {
  /**
   * 获取题目列表
   * 从服务器获取答题活动的题目数据
   * 
   * @param {Object} formData - 请求参数对象
   * @param {string} formData.openid - 用户的openid
   * @returns {Promise<Object>} 题目列表响应对象
   * @returns {boolean} returns.success - 请求是否成功
   * @returns {Array} returns.data.questions - 题目数组
   * @returns {Object} returns.data.questions[0] - 单个题目对象
   * @returns {string} returns.data.questions[0].text - 题目内容
   * @returns {Array} returns.data.questions[0].options - 选项数组
   * @returns {number} returns.data.questions[0].correct - 正确答案索引
   */
  getQuizQuestions: async (formData) => {
    try {
      //formData.examId='1983748279057367042';
      // 实际环境中应使用的真实API调用
      // return await api.get('/quiz/get-questions', { params: formData });
      const response= await api.post('/api/front/wechat/create-paper',formData);
      const paperId=response.data.id;
      formData.id=paperId;
      return await api.post('/api/front/wechat/paper-detail',formData);
    } catch (error) {
      console.error('获取题目失败:', error);
      
      // 容错处理 - 返回默认题目数据
      return {
        success: true,
        data: {
          questions: [
            {
              text: '以下哪个不是人工智能的主要分支？',
              options: ['机器学习', '深度学习', '自然语言处理', '网络安全'],
              correct: 3
            },
            {
              text: '机器人三定律是谁提出的？',
              options: ['爱因斯坦', '阿西莫夫', '霍金', '图灵'],
              correct: 1
            },
            {
              text: '深度学习中常用的激活函数是？',
              options: ['Sigmoid', 'ReLU', 'Tanh', '以上都是'],
              correct: 3
            },
            {
              text: '图灵测试的目的是什么？',
              options: ['测试计算机的速度', '测试计算机是否具有智能', '测试网络连接', '测试内存容量'],
              correct: 1
            },
            {
              text: '以下哪种是机器人的感知系统？',
              options: ['伺服电机', '传感器', '减速器', '控制器'],
              correct: 1
            }
          ]
        }
      };
    }
  },
  
  /**
   * 获取单个题目详细信息
   * @param {Object} formData - 请求参数对象
   * @param {string} formData.openid - 用户的openid
   * @param {number} formData.questionIndex - 题目索引
   * @returns {Promise<Object>} 题目详细信息响应对象
   */
  getQuestionDetail: async (formData) => {
    try {
      // 调用后端API获取单个题目详细信息
      return await api.post('/api/front/wechat/qu-detail', formData);
    } catch (error) {
      console.error('获取题目详情失败:', error);
      throw error;
    }
  },
  
  /**
   * 提交题目答案
   * @param {Object} formData - 请求参数对象
   * @param {string} formData.openid - 用户的openid
   * @param {number} formData.questionIndex - 题目索引
   * @param {number} formData.answer - 用户选择的答案索引
   * @returns {Promise<Object>} 提交结果响应对象
   */
  submitAnswer: async (formData) => {
    try {
      // 调用后端API提交答案
      return await api.post('/api/front/wechat/fill-answer', formData);
    } catch (error) {
      console.error('提交答案失败:', error);
      throw error;
    }
  },

/**
   * 完成答题，提交结果，判断是否有抽奖次数
   * @param {Object} formData - 请求参数对象
   * @param {string} formData.openid - 用户的openid
   * @param {number} formData.questionIndex - 题目索引
   * @param {number} formData.answer - 用户选择的答案索引
   * @returns {Promise<Object>} 提交结果响应对象
   */
  finishQuiz: async (formData) => {
    try {
      // 调用后端API提交答案
      return await api.post('/api/front/wechat/hand-exam', formData);
    } catch (error) {
      console.error('提交答案失败:', error);
      throw error;
    }
  },


/**
   * 获取抽奖活动状态
   * 获取当前用户的抽奖状态
   * @param {Object} formData - 领奖表单数据对象
   * @param {string} formData.openid - 用户openid
   * @returns {Promise<Object>} 抽奖信息响应对象
   * @returns {boolean} returns.success - 请求是否成功
   * @returns {Object} returns.data - 抽奖数据对象
   * @returns {number} returns.data.remainingDraws - 用户剩余抽奖次数
   * @returns {Array} returns.data.prizes - 可用奖品列表
   */
  getLotteryStatus: async (formData) => {
    try {
      // 实际环境中应使用的真实API调用
       return await api.post('/api/front/wechat/queryActStatus',formData);
    } catch (error) {
      // 错误处理 - 记录错误日志
      console.error('获取抽奖信息失败:', error);
      // 容错处理 - 即使API调用失败也返回默认数据
      // 确保用户界面不会因为后端问题而完全崩溃
      return {
        success: true,
        data: {
          act_Status: false,
          act_Status_Msg: '活动未开始'
        }
      };
    }
  },


  /**
   * 获取抽奖信息
   * 获取当前用户的抽奖状态、剩余抽奖次数和奖品列表
   * @param {Object} formData - 领奖表单数据对象
   * @param {string} formData.openid - 用户openid
   * @returns {Promise<Object>} 抽奖信息响应对象
   * @returns {boolean} returns.success - 请求是否成功
   * @returns {Object} returns.data - 抽奖数据对象
   * @returns {number} returns.data.remainingDraws - 用户剩余抽奖次数
   * @returns {Array} returns.data.prizes - 可用奖品列表
   */
  getLotteryInfo: async (formData) => {
    try {
      // 实际环境中应使用的真实API调用
       return await api.post('/api/front/wechat/queryActInfo',formData);
      
      // Mock响应数据 - 开发阶段使用
      // 提供默认剩余抽奖次数为1次
      // return {
      //   success: true,
      //   data: {
      //     remainingDraws: 1,      // 用户剩余抽奖次数
      //     prizes: mockPrizes      // 奖品列表
      //   }
      // };
    } catch (error) {
      // 错误处理 - 记录错误日志
      console.error('获取抽奖信息失败:', error);
      
      // 容错处理 - 即使API调用失败也返回默认数据
      // 确保用户界面不会因为后端问题而完全崩溃
      return {
        success: true,
        data: {
          remainingDraws: 1,
          prizes: mockPrizes
        }
      };
    }
  },
  
  /**
   * 开始抽奖
   * 执行抽奖操作并返回抽奖结果
   * 
   * @param {Object} params - 请求参数
   * @param {string} params.openid - 用户的openid
   * @returns {Promise<Object>} 抽奖结果响应对象
   * @returns {boolean} returns.success - 请求是否成功
   * @returns {Object} returns.data - 抽奖结果数据
   * @returns {Object} returns.data.prize - 抽中的奖品详细信息
   * @returns {boolean} returns.data.isHighPrize - 是否为高价值奖品标志
   * @throws {Error} 抽奖失败时抛出错误
   */
  startDraw: async (params) => {
    try {
      // 调用后端抽奖API
      const response = await api.post('/api/front/wechat/toAward', params);
      
      // 如果API返回的是完整的抽奖结果，直接返回
      // 如果只返回了奖品信息，可以在这里构造完整的响应结构
      if (response && response.data) {
        console.log("抽中了啥",response.data)
        // 检查是否为高价值奖品（一等或二等奖）
        if (response.data.prize && response.data.prize.name) {
          response.data.isHighPrize = response.data.prize.name.includes('一等') || 
                                     response.data.prize.name.includes('二等');
        }
        return response;
      }
      
      return response;
    } catch (error) {
      // 错误处理 - 记录错误日志
      console.error('抽奖失败:', error);
      
      // 向上抛出错误，让调用方处理失败情况
      throw error;
    }
  }
};

/**
 * 奖品领取相关API方法集合
 * 封装所有与奖品领取和信息提交相关的接口调用
 */
export const prizeAPI = {
  /**
   * 验证公众号关注状态
   * 检查用户是否已关注活动公众号
   * 
   * @returns {Promise<Object>} 验证结果响应对象
   * @returns {boolean} returns.success - 请求是否成功
   * @returns {Object} returns.data - 验证数据
   * @returns {boolean} returns.data.isFollowing - 是否已关注公众号
   */
  verifyFollowStatus: async (formData) => {
    try {
      // 实际环境中应使用的真实API调用
      return await api.post('/api/front/wechat/getUser',formData);
    } catch (error) {
      // 错误处理 - 记录错误日志
      console.error('验证关注状态失败:', error);
      // 容错处理 - 默认返回未关注状态
      // 在无法确认用户关注状态时，要求用户先关注公众号更安全
      return {
        success: true,
        data: {
          isFollowing: false
        }
      };
    }
  },
  /**
   * 获取历史抽奖记录
   * @param {*} formData 
   * @returns 
   */
   getLotteryRecords: async (formData) => {
    try {
      // 实际环境中应使用的真实API调用
      return await api.post('/api/front/wechat/queryMyPrize',formData);
    } catch (error) {
      // 错误处理 - 记录错误日志
      console.error('验证关注状态失败:', error);
      // 容错处理 - 默认返回未关注状态
      // 在无法确认用户关注状态时，要求用户先关注公众号更安全
      return {
        success: true,
        data: []
      };
    }
  },
  /**
   * 提交领奖信息
   * 提交用户的领奖表单数据，完成奖品领取流程
   * 
   * @param {Object} formData - 领奖表单数据对象
   * @param {string} formData.name - 用户姓名
   * @param {string} formData.phone - 用户手机号码
   * @param {string} formData.address - 收货地址
   * @returns {Promise<Object>} 提交结果响应对象
   * @returns {boolean} returns.success - 请求是否成功
   * @returns {Object} returns.data - 提交结果数据
   * @returns {string} returns.data.message - 提交结果消息
   * @returns {string} returns.data.orderId - 生成的订单ID
   * @throws {Error} 表单验证失败或提交错误时抛出错误
   */
  submitPrizeInfo: async (formData) => {
    try {
      
      
      // 客户端数据验证
      // 确保必填字段都已填写
      if (!formData.realName || !formData.mobile || !formData.userAddress) {
        throw new Error('请填写完整的领奖信息');
      }
      
      // 手机号码格式验证
      // 使用正则表达式验证中国大陆手机号码格式
      if (!/^1[3-9]\d{9}$/.test(formData.mobile)) {
        throw new Error('请输入有效的手机号码');
      }
      // 实际环境中应使用的真实API调用
       return await api.post('/api/front/wechat/updateUserRewardInfo', formData);
      // Mock响应 - 模拟成功提交
      // return {
      //   success: true,
      //   data: {
      //     message: '提交成功',              // 提交结果消息
      //     orderId: 'ORD' + Date.now()       // 生成唯一订单ID，使用时间戳确保唯一性
      //   }
      // };
    } catch (error) {
      // 错误处理 - 记录错误日志
      console.error('提交领奖信息失败:', error);
      
      // 向上抛出错误，让调用方处理失败情况
      // 表单提交错误需要用户知道具体原因
      throw error;
    }
  }
};

/**
 * 身份验证相关API方法集合
 * 封装所有与用户认证相关的功能
 */
export const authAPI = {
  /**
   * 用户登录
   * 使用微信授权码进行登录验证
   * 
   * @param {string} code - 微信授权码(code)
   * @returns {Promise<Object>} 登录结果响应对象
   * @returns {boolean} returns.success - 请求是否成功
   * @returns {Object} returns.data - 登录结果数据
   * @returns {string} returns.data.token - 认证token，用于后续API调用的身份验证
   * @returns {Object} returns.data.userInfo - 用户基本信息对象
   * @returns {string} returns.data.userInfo.nickname - 用户昵称
   * @returns {string} returns.data.userInfo.avatar - 用户头像URL
   * @throws {Error} 登录失败时抛出错误
   */
  login: async (code) => {
    try {
      // 实际环境中应使用的真实API调用
      // 需要将微信授权码code发送到后端进行验证
      // return await api.post('/auth/login', { code });
      
      // Mock响应 - 模拟登录成功
      // 生成一个基于时间戳的模拟token
      const mockToken = 'mock_token_' + Date.now();
      
      // 将token存储到本地，供后续请求使用
      localStorage.setItem('auth_token', mockToken);
      
      return {
        success: true,
        data: {
          token: mockToken,        // 认证token
          userInfo: {              // 用户基本信息
            nickname: '测试用户',   // 用户昵称
            avatar: ''             // 用户头像URL（为空）
          }
        }
      };
    } catch (error) {
      // 错误处理 - 记录错误日志
      console.error('登录失败:', error);
      
      // 向上抛出错误，让调用方处理登录失败情况
      throw error;
    }
  },
  
  /**
   * 检查登录状态
   * 判断用户当前是否已登录
   * 
   * @returns {boolean} 是否已登录
   */
  checkLoginStatus: () => {
    // 通过检查本地存储中是否存在auth_token来判断登录状态
    // 使用双重取反操作将值转换为布尔类型
    return !!localStorage.getItem('auth_token');
  },
  
  /**
   * 用户登出
   * 清除用户登录状态
   */
  logout: () => {
    // 从本地存储中移除auth_token，清除用户认证状态
    localStorage.removeItem('auth_token');
  }
};
// 只导出processImageUrl函数，其他API对象已经在定义时导出了
export { processImageUrl };

// 导出默认API实例
export default api;